import classNames from 'classnames';
import { isValidElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Primary } from '../../styles/Colors';
import { ReactComponent as BackwardIcon } from '../assets/icons/BackwardIcon.svg';
import { ReactComponent as ForwardIcon } from '../assets/icons/ForwardIcon.svg';
import { ReactComponent as PauseIcon } from '../assets/icons/PauseIcon.svg';
import { ReactComponent as PlayIcon } from '../assets/icons/PlayIcon.svg';
import { ReactComponent as RepeatIcon } from '../assets/icons/RepeatIcon.svg';
import { ReactComponent as StopIcon } from '../assets/icons/StopIcon.svg';
import IconButton from '../Button/IconButton';
import { ControlAction, ControlButtonProps } from './types';

interface ControlItemProps {
  action: ControlAction;
}

const ControlIcon = ({ action }: ControlItemProps) => {
  switch (action) {
    case 'stop':
      return <StopIcon className="sup-icon-stop" />;
    case 'play':
      return <PlayIcon className="sup-icon-play" />;
    case 'pause':
      return <PauseIcon className="sup-icon-pause" />;
    case 'repeat':
      return <RepeatIcon className="sup-icon-repeat" />;
    case 'backward':
      return <BackwardIcon className="sup-icon-backward" />;
    case 'forward':
      return <ForwardIcon className="sup-icon-forward" />;
    default:
      return null;
  }
};

/**
 * The i18n key that names each transport action (ISSUE-062).
 *
 * Keyed by the `ControlAction` union rather than a loose string map, so adding
 * a seventh action is a compile error here instead of a seventh silently
 * nameless button in the DOM -- the same guard shape ISSUE-060 used for the
 * audio-editor toolbar.
 */
const CONTROL_LABELS: Record<ControlAction, string> = {
  stop: 'Stop',
  play: 'Play',
  pause: 'Pause',
  repeat: 'Repeat',
  backward: 'Skip backward',
  forward: 'Skip forward',
};

export const ControlButton = ({
  isControllable = false,
  action,
  color,
  disabled,
  onClick,
  isActive,
  icon,
}: ControlButtonProps) => {
  // Block body (was an expression body) so the component can call the hook --
  // the name has to come from `t` and re-read on a language change.
  const { t } = useTranslation();

  return (
    <IconButton
      variant="none"
      aria-label={t(CONTROL_LABELS[action]) as string}
      color={color || Primary[400]}
      disabled={!isControllable || disabled}
      onClick={onClick}
      className={classNames({
        active: isActive,
      })}
    >
      {isValidElement(icon) ? icon : <ControlIcon action={action} />}
    </IconButton>
  );
};
